
import React, { useEffect, useState } from "react";
import { FiSearch, FiEdit, FiTrash2, FiX } from "react-icons/fi";
import { IoApps, IoList } from "react-icons/io5";
import { FaMapMarkerAlt } from "react-icons/fa";
import axios from "../../auth/auth";


// Dummy customers (used only if backend returns empty)
const dummyCustomers = [
    {
        CustomerID: "D1",
        CustomerName: "Yankee Stadium",
        Corporation: "Yankee Stadium",
        Garages: 0,
        Address: "Yankee Stadium New York",
        Location: "New York",
    },
    {
        CustomerID: "D2",
        CustomerName: "United Parking LLC",
        Corporation: "United Parking LLC",
        Garages: 12,
        Address: "365 Smith Street Providence, RI 02908",
        Location: "Providence",
    },
    {
        CustomerID: "D3",
        CustomerName: "Parking Payments - Park To Visit",
        Corporation: "Parking Payments - Park To Visit",
        Garages: 27,
        Address: "1314 E. Las Olas Blvd Ft. Lauderdale FL 33301",
        Location: "Florida",
    },
];

const emptyForm = {
    CustomerID: "",
    CustomerName: "",
    Corporation: "",
    Location: "",
    Address: "",

    RepresentativeName: "",
    RepresentativeTitle: "",
    RepresentativeEmail: "",
    RepresentativeContact: "",

    TransientRate: "",
    FreeTransientAllowed: "",
    TransientFreeRate: "",
    FreeTransientFreeAllowed: "",
    ReservationsRate: "",
    FreeReservationsAllowed: "",
    RatePerPermit: "",
    FreePermitAllowed: "",
    RatePerApp: "",
    FreeAppAllowed: "",
    EventsRate: "",
    FreeEventsAllowed: "",
    PassesRate: "",
    FreePassesAllowed: "",
    MonthlyFixedCharges: "",
    CreditCardFeedRate: "",
    GatewayFees: "",
    ActiveLicenses: "",
    citation_charges: "",
    amount_collected_charges: "",
    parking_validation_service: ""
};


export default function CustomersTab() {
    
    const [customers, setCustomers] = useState([]);
    const [view, setView] = useState("cards");
    const [formOpen, setFormOpen] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [garageCount, setGarageCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const userRole = typeof window !== "undefined" ? sessionStorage.getItem("role") : null;
    const disabledPricingFields = [
        "TransientRate", "FreeTransientAllowed", "TransientFreeRate", "FreeTransientFreeAllowed",
        "ReservationsRate", "FreeReservationsAllowed",
        "RatePerPermit", "FreePermitAllowed",
        "RatePerApp", "FreeAppAllowed",
        "EventsRate", "FreeEventsAllowed",
        "PassesRate", "FreePassesAllowed",
        "MonthlyFixedCharges", "CreditCardFeedRate", "GatewayFees", "ActiveLicenses",
        "citation_charges", "amount_collected_charges", "parking_validation_service"
    ];

    const isFieldDisabled = (key) => userRole === "Sales" && disabledPricingFields.includes(key);

    // -----------------------
    // Ensure numeric values are numbers
    // -----------------------
    const fixTypes = (data) => {
        const numericFields = [
            "TransientRate", "FreeTransientAllowed",
            "TransientFreeRate", "FreeTransientFreeAllowed",
            "ReservationsRate", "FreeReservationsAllowed",
            "RatePerPermit", "FreePermitAllowed",
            "RatePerApp", "FreeAppAllowed",
            "EventsRate", "FreeEventsAllowed",
            "PassesRate", "FreePassesAllowed",
            "MonthlyFixedCharges", "CreditCardFeedRate",
            "GatewayFees", "ActiveLicenses",
            "citation_charges", "amount_collected_charges", "parking_validation_service"
        ];

        const fixed = { ...data };
        numericFields.forEach((key) => {
            if (fixed[key] === "" || fixed[key] === null) {
                fixed[key] = 0;
            } else {
                fixed[key] = Number(fixed[key]);
            }
        });

        return fixed;
    };
    const loadGarages = async () => {
        try {
            const res = await axios.get("/garages/");
            setGarageCount(res.data.length);  // dynamically count garages
        } catch (err) {
            console.error("Error loading garages", err);
        }
    };
    const loadCustomers = async () => {
        try {
            const res = await axios.get("/customers/");

            if (!res.data || res.data.length === 0) {
                setCustomers(dummyCustomers);
            } else {
                setCustomers(res.data);
            }
        } catch (err) {
            console.log("Backend error, showing dummy data");
            setCustomers(dummyCustomers);
        }
    };

    // useEffect(() => {
    //     loadCustomers();
    //     loadGarages();
    // }, []);

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);

                await Promise.all([
                    loadCustomers(),
                    loadGarages()
                ]);

            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);


    const openForm = (customer = null) => {
        if (customer) {
            setFormData({ ...customer, mode: "edit" });
        } else {
            setFormData({ ...emptyForm, mode: "create" });
        }
        setFormOpen(true);
    };

    const handleSave = async () => {
        if (!formData.CustomerID || !formData.CustomerID.trim()) {
            alert("Customer ID is required.");
            return;
        }

        if (!formData.CustomerName || !formData.CustomerName.trim()) {
            alert("Customer Name is required.");
            return;
        }

        if (!formData.Address || !formData.Address.trim()) {
            alert("Address is required.");
            return;
        }

        try {
            const payload = fixTypes(formData);

            if (formData.mode === "edit") {
                // UPDATE existing customer
                await axios.put(`/customers/${formData.CustomerID}`, payload);
            } else {
                // CREATE new customer
                await axios.post("/customers/create_customers", payload);
            }

            setFormOpen(false);
            loadCustomers();
        } catch (err) {
            console.error(err.response?.data || err);

            if (err.response?.status === 400 && err.response?.data?.detail === "Customer ID already exists") {
                alert("Customer ID already exists. Please use a different ID.");
            } else if (err.response?.status === 403) {
                alert(err.response?.data?.detail || "Access denied.");
            } else {
                alert("Error saving data. Missing or invalid fields?");
            }
        }
    };

    const deleteCustomer = async (id) => {
        if (!window.confirm("Delete this Customer?")) return;
        try {
            await axios.delete(`/customers/${id}`);
            loadCustomers();
        } catch (err) {
            console.error("Failed to delete customer:", err);
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Failed to delete customer.";
            alert(errorMessage);
        }

    };


    if (loading) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-600 text-sm">Loading customers...</p>
            </div>
        </div>
    );
}

    // Filter customers based on search term
    const filteredCustomers = customers.filter(customer =>
        customer.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.Location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.Corporation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.Address?.toLowerCase().includes(searchTerm.toLowerCase())
    );


    return (
        <div className="p-2">

            {/* KPI CARDS */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white shadow rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Customers</p>
                    <p className="text-2xl font-semibold">{filteredCustomers.length}</p>
                </div>

                <div className="bg-white shadow rounded-lg p-4">
                    <p className="text-sm text-gray-500">Active Garages</p>
                    <p className="text-2xl font-semibold">{garageCount}</p>

                </div>

                <div className="bg-white shadow rounded-lg p-4">
                    <p className="text-sm text-gray-500">Avg. Garages per Customer</p>
                    <p className="text-2xl font-semibold">{garageCount > 0
                        ? (filteredCustomers.length / garageCount).toFixed(2)
                        : "0.00"}</p>
                </div>
            </div>

            {/* SEARCH + CONTROLS */}
            <div className="flex justify-between items-center mb-4">
                <div className="relative w-96">
                    <FiSearch className="absolute left-3 top-3 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search customers by name, location, corporation..."
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => setView("cards")}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${view === "cards" ? "bg-blue-600 text-white" : "bg-white"
                            }`}
                    >
                        <IoApps /> Cards
                    </button>

                    <button
                        onClick={() => setView("table")}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${view === "table" ? "bg-blue-600 text-white" : "bg-white"
                            }`}
                    >
                        <IoList /> Table
                    </button>

                    <button
                        onClick={() => openForm()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                    >
                        + Add Customer
                    </button>
                </div>
            </div>

            {/* CARD VIEW */}
            {view === "cards" && (
                <div className="grid grid-cols-3 gap-4">
                    {filteredCustomers.map((cust) => (
                        <div
                            key={cust.CustomerID}
                            className="bg-white shadow p-6 rounded-xl border border-gray-100"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-lg font-semibold">{cust.CustomerName}</h2>
                                    <p className="text-sm text-gray-500">{cust.Corporation}</p>
                                    {cust.RepresentativeName && (
                                        <p className="text-sm text-gray-400">
                                            SPOC: {cust.RepresentativeName} ({cust.RepresentativeTitle})
                                        </p>
                                    )}
                                </div>

                                <span className="text-xs px-3 py-1 bg-blue-100 text-blue-600 rounded-full">
                                    🏢 {cust.Garages || 0} Garages
                                </span>
                            </div>

                            <div className="flex items-center gap-2 my-3 text-gray-500">
                                <FaMapMarkerAlt />
                                <p>{cust.Address}</p>
                            </div>

                            <hr className="my-3" />

                            <p className="text-sm text-gray-400 mb-3">
                                Updated {new Date().toLocaleDateString()}
                            </p>

                            <div className="flex justify-end gap-3">
                                <button
                                    className="px-4 py-1 rounded bg-gray-100 flex items-center gap-1"
                                    onClick={() => openForm(cust)}
                                >
                                    <FiEdit /> Edit
                                </button>

                                <button
                                    className="px-4 py-1 rounded bg-red-100 text-red-600 flex items-center gap-1"
                                    onClick={() => deleteCustomer(cust.CustomerID)}
                                >
                                    <FiTrash2 /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* TABLE VIEW */}
            {view === "table" && (
                <table className="w-full bg-white shadow rounded-lg">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="p-3 text-left">Name</th>
                            <th className="p-3 text-left">Location</th>
                            <th className="p-3 text-left">Address</th>
                            <th className="p-3 text-center">SPOC</th>
                            <th className="p-3 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCustomers.map((cust) => (
                            <tr key={cust.CustomerID} className="border-b">
                                <td className="p-3">{cust.CustomerName}</td>
                                <td className="p-3">{cust.Location}</td>
                                <td className="p-3">{cust.Address}</td>
                                <td className="p-3">
                                    {cust.RepresentativeName}<br />
                                    <span className="text-xs text-gray-500">
                                        {cust.RepresentativeEmail}
                                    </span>
                                </td>
                                <td className="p-3 flex gap-3">
                                    <button className="text-blue-600" onClick={() => openForm(cust)}>
                                        <FiEdit />
                                    </button>
                                    <button
                                        className="text-red-600"
                                        onClick={() => deleteCustomer(cust.CustomerID)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* ADD / EDIT FORM */}

            {formOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center overflow-auto">
                    <div className="bg-white w-[900px] max-h-[90vh] overflow-y-auto p-8 rounded-xl shadow-lg">

                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold">
                                {formData.CustomerID ? "Edit Customer" : "Add New Customer"}
                            </h2>
                            <button
                                onClick={() => setFormOpen(false)}
                                className="text-gray-500 hover:text-gray-700 p-1"
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        <p className="text-xs text-gray-500 mb-4">
                            Fields marked with <span className="text-red-500">*</span> are required
                        </p>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="text-sm text-gray-600">
                                    Customer ID <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    className="border w-full p-2 rounded"
                                    disabled={formData.mode === "edit"}
                                    value={formData.CustomerID}
                                    onChange={(e) =>
                                        setFormData({ ...formData, CustomerID: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">
                                    Customer Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="border w-full p-2 rounded"
                                    required
                                    value={formData.CustomerName}
                                    onChange={(e) =>
                                        setFormData({ ...formData, CustomerName: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">Corporation</label>
                                <input
                                    type="text"
                                    className="border w-full p-2 rounded"
                                    value={formData.Corporation}
                                    onChange={(e) =>
                                        setFormData({ ...formData, Corporation: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">Location</label>
                                <input
                                    type="text"
                                    className="border w-full p-2 rounded"
                                    value={formData.Location}
                                    onChange={(e) =>
                                        setFormData({ ...formData, Location: e.target.value })
                                    }
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="text-sm text-gray-600">
                                    Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    className="border w-full p-2 rounded"
                                    value={formData.Address}
                                    onChange={(e) =>
                                        setFormData({ ...formData, Address: e.target.value })
                                    }
                                />
                            </div>

                            {/* Representative Information */}
                            <h3 className="text-lg font-semibold mb-2 col-span-2 mt-4">
                                Customer Manager Information
                            </h3>

                            <div>
                                <label className="text-sm text-gray-600">Representative Name</label>
                                <input
                                    type="text"
                                    className="border w-full p-2 rounded"
                                    value={formData.RepresentativeName || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, RepresentativeName: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">Title / Role</label>
                                <input
                                    type="text"
                                    className="border w-full p-2 rounded"
                                    value={formData.RepresentativeTitle || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, RepresentativeTitle: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">Email ID</label>
                                <input
                                    type="email"
                                    className="border w-full p-2 rounded"
                                    value={formData.RepresentativeEmail || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, RepresentativeEmail: e.target.value })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600">Contact Number</label>
                                <input
                                    type="text"
                                    className="border w-full p-2 rounded"
                                    value={formData.RepresentativeContact || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, RepresentativeContact: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <h3 className="text-lg font-semibold mb-4">Pricing Configuration</h3>

                        <div className="grid grid-cols-3 gap-4">
                            {[
                                ["TransientRate", "Transient Rate ($)"],
                                ["FreeTransientAllowed", "Free Transient Allowed"],
                                ["TransientFreeRate", "Transient Free Rate ($)"],
                                ["FreeTransientFreeAllowed", "Free Transient Free Allowed"],
                                ["ReservationsRate", "Reservations Rate ($)"],
                                ["FreeReservationsAllowed", "Free Reservations Allowed"],
                                ["RatePerPermit", "Rate per Permit ($)"],
                                ["FreePermitAllowed", "Free Permit Allowed"],
                                ["RatePerApp", "Licenses Rate ($)"],
                                ["FreeAppAllowed", "Free App Allowed"],
                                ["EventsRate", "Events Rate ($)"],
                                ["FreeEventsAllowed", "Free Events Allowed"],
                                ["PassesRate", "Passes Rate ($)"],
                                ["FreePassesAllowed", "Free Passes Allowed"],
                                ["MonthlyFixedCharges", "Monthly Fixed Charges ($)"],
                                ["CreditCardFeedRate", "Credit Card Fee Rate"],
                                ["GatewayFees", "Gateway Fees ($)"],
                                ["ActiveLicenses", "No. of Active Licenses"],
                                ["citation_charges", "Citation Charges ($)"],
                                ["amount_collected_charges", "Amount Collected Charges ($)"],
                                ["parking_validation_service", "Parking Validation Service ($)"],
                                ].map(([key, label]) => {
                                    const disabled = isFieldDisabled(key);
                                    return (
                                        <div key={key}>
                                            <label className="text-sm text-gray-600">{label}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                disabled={disabled}
                                                className={`border w-full p-2 rounded ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                                                value={formData[key]}
                                                onChange={(e) => {
                                                    if (disabled) return;

                                                    let value = e.target.value;

                                                    // Prevent negative (typed or pasted)
                                                    if (value < 0) value = 0;

                                                    setFormData({
                                                        ...formData,
                                                        [key]: value
                                                    });
                                                }}
                                            />

                                        </div>
                                    );
                                })}
                        </div>

                        <div className="flex justify-end gap-4 mt-6">
                            <button
                                className="px-4 py-2 bg-gray-200 rounded"
                                onClick={() => setFormOpen(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded"
                                onClick={handleSave}
                            >
                                Save
                            </button>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
}
